/**
 * Sursa UNICA de adevar pentru redirectul post-autentificare.
 *
 * De ce exista: pana acum cinci locuri decideau independent unde ajunge un user dupa login
 * (`useLoginWithForm`, `useAuthRedirects`, `auth-provider-v2.performAuthorizedRedirect`,
 * `UnauthenticatedLayout`, plus hook-uri de formular). `setTimeout`-ul din `useLoginWithForm`
 * castiga mereu cursa, fiind ultimul si in afara lantului de efecte React, deci logica pe stagii
 * din celelalte trei nu apuca sa se aplice la login.
 *
 * Functia e PURA si fara hook-uri, ca sa poata fi testata fara provideri montati. Contextul
 * (`PATHS`, `PAGES`, `ROLES`, `getUserHomePage`) se injecteaza de la apelant, care il ia din
 * `useRoutesProvider()`.
 */

import type { UserInfo } from '../../../providers/auth-provider.types';
import type { Role, RolesType } from '../../../providers/routes-provider.types';

export interface PostAuthContext {
  PATHS: Record<string, string>;
  PAGES: Record<string, string>;
  /**
   * Deviatie fata de spec (`Record<string, string>`): `RolesType` e o *interfata*, iar TS nu
   * deduce index signature implicit pentru interfete — deci `ROLES` din `useRoutesProvider()`
   * n-ar fi atribuibil unui `Record<string, string>` (TS2322).
   */
  ROLES: RolesType;
  /**
   * Deviatie fata de spec (`(role?: string) => string`): tipul real din routes-provider e
   * `(role?: Role) => string`, iar sub `strictFunctionTypes` cel real NU e atribuibil variantei
   * cu `string` (contravarianta pe parametru). Pastram `Role` ca sa nu ceara cast la cele patru
   * locuri de apel; ambele tipuri sunt uniuni de literali, deci testele n-au nevoie de provideri.
   */
  getUserHomePage: (role?: Role) => string;
  /**
   * Comutatoarele de build ale pasilor 3 (video) si 4 (protocol) din wizard (p8). Opționale,
   * implicit ambele oprite -- fara ele, comportamentul e IDENTIC cu cel de dinainte de p8: pasul
   * 5 (cerere in asteptare) ateriza direct pe pagina de asteptare, ca azi.
   */
  pasiInscriere?: PasiInscriere;
}

export interface PasiInscriere {
  videoActiv: boolean;
  protocolActiv: boolean;
}

/**
 * Calculeaza unde trebuie sa ajunga userul, sau `null` daca nu trebuie redirectat.
 *
 * @param user     userul decodat din JWT; `null` inseamna neautentificat -> `null`
 * @param pathname ruta curenta. `null` inseamna „tocmai s-a logat, nu conteaza unde e acum" —
 *                 in acest caz se intoarce mereu o tinta, niciodata `null` pentru un user valid.
 *                 Cand pathname E dat, functia raspunde la alta intrebare: „trebuie MUTAT de
 *                 aici?". Un user cu acces complet primeste `null` = „poate sta unde e"; doar
 *                 gardele de stagiu (elev fara acord, profesor neaprobat) muta de oriunde.
 * @param ctx      `PATHS` / `PAGES` / `ROLES` / `getUserHomePage` din routes-provider
 */
export function resolvePostAuthTarget(
  user: UserInfo | null,
  pathname: string | null,
  ctx: PostAuthContext
): string | null {
  if (!user?.role) return null;

  const { PATHS, PAGES, ROLES, getUserHomePage } = ctx;

  /**
   * GARDA: trimite userul la `path` indiferent unde e, pentru ca n-are voie unde se afla.
   * Intoarce `null` doar daca e deja acolo — altfel se intra in bucla de navigare.
   */
  const target = (path: string): string | null =>
    pathname !== null && pathname === path ? null : path;

  /**
   * ATERIZARE: unde ajunge un user care are voie peste tot.
   *
   * ⚠️ Intoarce home-ul DOAR imediat dupa login (`pathname === null`). In timpul navigarii
   * normale intoarce `null`, adica „poti sta unde esti".
   *
   * De ce e esential sa NU fie `target(home)`: `useAuthRedirects` apeleaza functia la FIECARE
   * render, iar `AuthenticatedLayout` navigheaza ori de cate ori rezultatul difera de ruta
   * curenta. Cu `target(home)` aici, un ADMIN care deschidea `/mesaje` primea `home !== /mesaje`
   * -> bounce inapoi pe home, la nesfarsit. Efect: rolurile cu acces complet erau PINUITE pe
   * home si nu puteau naviga nicaieri (gasit 2026-08-21).
   *
   * Nu se pierde nicio verificare: permisiunea pe ruta se face separat, pe `ALLOWED_ROLES`, in
   * `auth-provider-v2.validateTokenChange` si in `AuthenticatedLayout`.
   */
  const landing = (): string | null =>
    pathname === null ? getUserHomePage(user.role) : null;

  // ---- ELEV -------------------------------------------------------------
  if (user.role === ROLES.ELEV) {
    // Elevul dezactivat nu poate face nimic pana nu are acordul parintilor.
    if (!user.isEnabled) {
      return target(PATHS[PAGES.PARENTAL_APPROVAL]);
    }
    // Elevul tocmai activat nu mai are ce cauta pe formularul de acord — si-a facut treaba.
    // Garda punctuala, pe O SINGURA ruta; nu o aterizare generala, care l-ar pinui pe home.
    if (pathname !== null && pathname === PATHS[PAGES.PARENTAL_APPROVAL]) {
      return getUserHomePage(user.role);
    }
    return landing();
  }

  // ---- PROFESOR ---------------------------------------------------------
  if (user.role === ROLES.PROFESOR) {
    // (1) Rute exceptate: profesorul are voie sa stea aici indiferent de stagiu.
    //     Pagina de confirmare e exceptata DOAR cat timp confirmarea e ceruta (2.19). Exceptata
    //     neconditionat, un link vechi lasa un profesor aprobat / in asteptare / respins pe un
    //     ecran care nu-l priveste, iar singura plasa era un setTimeout din hook-ul formularului.
    //
    //     Tabelul ministerului (`/raport-institutii-scolare-2025-2026`) nu mai exista: pe 17.09
    //     (FE-tabel-MEC) a iesit din exceptii -- wizardul cauta scoala prin
    //     `/api/schools/import-data`, nu prin el, si nu era legat de nicaieri in UI -- iar pe
    //     2026-09-21 ruta a fost SCOASA de tot, prin decizia owner-ului: `/api/schools/report` e
    //     inchis pentru PROFESOR (filtrul pe contact era oracol pe datele colegilor). Raportul de
    //     scoli ramane doar ADMIN/DBEST, pe `SCHOOLS_REPORT`.
    const exemptPaths =
      user.needsSchoolRegistrationConfirmation === true
        ? [PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION]]
        : [];
    if (pathname !== null && exemptPaths.includes(pathname)) {
      return null;
    }

    // (2) Profesor nou: n-a completat formularul si n-are scoala. (5b) acopera restul cazurilor fara scoala.
    if (!user.hasSchoolRegistrationFormCompleted && !user.schoolId) {
      return target(PATHS[PAGES.SCHOOL_REQUEST_FORM]);
    }

    // (3) Cerere respinsa in editia curenta -> inapoi pe formular, ca sa corecteze.
    //     INAINTEA lui (4): un profesor respins nu are ce confirma.
    if (user.schoolRequestRejectedForCurrentEdition === true) {
      return target(PATHS[PAGES.SCHOOL_REQUEST_FORM]);
    }

    // (4) Profesor din editia trecuta: trebuie sa confirme participarea.
    if (user.needsSchoolRegistrationConfirmation === true) {
      return target(PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION]);
    }

    // (5) Cerere trimisa, inca nedecisa: doar pagina de asteptare + profil/parola.
    if (user.hasPendingSchoolRequest === true) {
      const { videoActiv = false, protocolActiv = false } = ctx.pasiInscriere ?? {};

      // POARTA TARE pe video (p8): obligatoriu, deci muta de ORIUNDE -- chiar de pe
      // profil/parola -- pana e vazut. Fara flag, `videoNevazut` e mereu false, adica
      // exact comportamentul de dinainte de p8.
      const videoNevazut = videoActiv && user.videoInscriereVazut !== true;
      if (videoNevazut) {
        return target(PATHS[PAGES.VIDEO_INSCRIERE]);
      }

      const allowedPaths = [
        PATHS[PAGES.PROFILE],
        PATHS[PAGES.PASSWORD_CHANGE],
        PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER],
        // Pasul de video (U4): fluxul clientului il pune INAINTE de aprobare, deci
        // exact in starea asta. Fara flagul de frontend ruta nici nu se
        // inregistreaza, deci linia e inerta — nu deschide nimic singura.
        PATHS[PAGES.VIDEO_INSCRIERE],
        // Pasul 4 din 4, protocolul (SP-03): acelasi motiv ca videoul de deasupra —
        // se preda INAINTE de aprobare, deci exact in starea asta. Si aici linia e
        // inerta fara flagul de frontend, pentru ca ruta nu se inregistreaza.
        // 🔴 Adaugata AICI, in singurul decident, nu printr-o garda noua: cinci
        // decidenti independenti au pinuit odata trei roluri pe home.
        PATHS[PAGES.PROTOCOL_INSCRIERE],
      ];
      if (pathname !== null && allowedPaths.includes(pathname)) {
        return null;
      }

      // POARTA MOALE pe protocol (p8): NU muta de pe profil/parola (verificat mai sus),
      // doar decide ATERIZAREA implicita cand nu era nicaieri anume.
      //
      // 🔴 Doar cand protocolul CHIAR se poate descarca (punctul 0.5, owner 2026-09-19). In
      // starea „neaprobat" butonul din `CardProtocol` e dezactivat, iar acel 409 vine exact
      // din lipsa claim-ului `schoolId` (`ProtocolDeColaborareController` -> `requireSchoolId()`
      // -> `ContIncompletException`). Fara conditia de mai jos, poarta ateriza contul fara
      // scoala pe un ecran care refuza sa dea ce poarta i-a promis.
      //
      // `schoolId` prezent ⇔ contul are scoala ⇔ protocolul se poate descarca. E acelasi
      // discriminator ca 409-ul, citit din acelasi loc de catre server, nu o aproximare a lui
      // (un al doilea criteriu — rol, `isApproved` — ar putea divergea de server in tacere).
      const protocolNedescarcat =
        protocolActiv && Boolean(user.schoolId) && user.protocolDescarcat !== true;
      if (protocolNedescarcat) {
        return target(PATHS[PAGES.PROTOCOL_INSCRIERE]);
      }

      return target(PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER]);
    }

    // (5b) Zero flag-uri si fara scoala: nu e nici nou (are formularul bifat), nici in
    //      asteptare, nici respins, nici de confirmat — dar n-are scoala. Asa arata un profesor
    //      PRELUAT (2.11) sau unul care a refuzat participarea. Fara regula asta ateriza pe home,
    //      cu acces complet si fara scoala, indistinctibil de un cont aprobat.
    //      DUPA (5), ca cererea in asteptare sa aiba prioritate; INAINTE de (6), evident.
    if (!user.schoolId) {
      return target(PATHS[PAGES.SCHOOL_REQUEST_FORM]);
    }

    // (6) Profesor aprobat: acces complet.
    //     Garda punctuala, pe O SINGURA ruta — acelasi tipar ca elevul activat de pe
    //     /acord-parinti: confirmarea nu mai e ceruta, deci pagina n-are ce sa-i arate. NU e o
    //     aterizare generala (aia l-ar pinui pe home, vezi `landing`).
    if (pathname !== null && pathname === PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION]) {
      return getUserHomePage(user.role);
    }
    return landing();
  }

  // ---- ADMIN / DBEST / LOGISTIC -----------------------------------------
  // Explicit, nu prin fallthrough: pana acum cadeau implicit si nu se vedea ca sunt tratate.
  if (
    user.role === ROLES.ADMIN ||
    user.role === ROLES.DBEST ||
    user.role === ROLES.LOGISTIC
  ) {
    return landing();
  }

  return null;
}

/**
 * Tinta polling-ului de pe paginile de asteptare (`useAccountWaiting`), sau `null` = ramane.
 *
 * Intai intrebarea de GARDA (pe ruta curenta), abia apoi — doar daca cererea nu mai e in
 * asteptare — cea de ATERIZARE. Cu aterizarea pusa direct, poarta moale pe protocol muta un
 * profesor in asteptare de pe propria pagina de asteptare pe `/protocol`, iar «Gata» de pe
 * protocol devenea o bucla (protocolul nu se poate descarca inainte de aprobare). Garda singura
 * nu ajunge: pentru un profesor aprobat intoarce `null`, deci aprobarea n-ar mai fi detectata.
 */
export function resolveTintaPeAsteptare(
  user: UserInfo,
  pathname: string,
  ctx: PostAuthContext
): string | null {
  const mutare = resolvePostAuthTarget(user, pathname, ctx);
  if (mutare !== null) return mutare;
  if (user.hasPendingSchoolRequest === true) return null;
  const aterizare = resolvePostAuthTarget(user, null, ctx) ?? ctx.getUserHomePage(user.role);
  return aterizare === pathname ? null : aterizare;
}
